// hooks/useExperiences.ts

import { useCallback, useState } from "react";
import {
  WaytoraExperienceInitial,
  ViatorProductInitial,
  ItineraryItem,
  TripLodging,
  Location,
  ViatorSearchResult,
  WaytoraExperienceListResponse,
} from "@/types";
import { useQueryClient } from "@tanstack/react-query";
import useExperiencesStore from "@/stores/tripBuilder/useExperiences";
import api from "@/config/axiosConfig";
import logger from "@/utils/logger";

export const useExperiences = (location: Location) => {
  const [isFetching, setIsFetching] = useState(false);
  const queryClient = useQueryClient();
  const {
    selectedExperienceId,
    setSelectedExperienceId,
    waytoraExperience,
    viatorProduct,
    setWaytoraExperience,
    setViatorProduct,
    isModalOpen,
    setIsModalOpen,
    setShouldCloseModal,
    activeExperiencesTab,
    setActiveExperiencesTab,
    selectedExperienceTabIndex,
    setSelectedExperienceTabIndex,
  } = useExperiencesStore();

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setWaytoraExperience(null);
    setViatorProduct(null);
    setSelectedExperienceId(null);
  }, [
    setIsModalOpen,
    setWaytoraExperience,
    setViatorProduct,
    setSelectedExperienceId,
  ]);

  const handleOpenModal = useCallback(
    async (item: ItineraryItem | TripLodging | ViatorSearchResult) => {
      // Check if modal is open and close it if necessary
      if (isModalOpen) {
        handleCloseModal();
      }

      logger.log("Item:", item);

      setShouldCloseModal(false);
      setIsFetching(true);
      setSelectedExperienceId(item.id as string);

      if (item.type === "waytora") {
        // Find all queries related to waytora experiences for this location
        const allWaytoraQueries = queryClient
          .getQueryCache()
          .findAll({ queryKey: ["waytoraExperiences", location.waytora_id] });

        let foundExperience: WaytoraExperienceInitial | null = null;

        // Iterate through the matched queries and their data
        for (const query of allWaytoraQueries) {
          const queryData = query.state.data as
            | WaytoraExperienceListResponse
            | undefined;
          if (queryData?.data) {
            // Search within the data array of the current query page
            foundExperience =
              queryData.data.find(
                (exp) =>
                  exp.id ===
                  Number((item as ItineraryItem).sourceId.waytoraPlaceId)
              ) || null;
            if (foundExperience) {
              break; // Stop searching once found
            }
          }
        }

        if (!foundExperience) {
          logger.log("Experience not found in cache, fetching from the API...");
          // Fetch from the API if not found in cache
          const response = await api.experiences.get("/general", {
            params: {
              place_id: (item as ItineraryItem).sourceId.waytoraPlaceId,
              basic: true,
            },
          });
          foundExperience = response.data;
        }

        setSelectedExperienceTabIndex(1);
        setActiveExperiencesTab("experiences");
        setWaytoraExperience(foundExperience);
        setViatorProduct(null);

        // Add timeout before opening modal
        setTimeout(() => {
          setIsModalOpen(true);
          setIsFetching(false);
        }, 500);
      } else if (item.type === "viator") {
        const viatorData = queryClient.getQueryData<{
          pageParams: any[];
          pages: {
            data: ViatorProductInitial[];
            nextCursor: number | null;
          }[];
        }>(["viatorProducts", location.viator_id]);

        let foundProduct: ViatorProductInitial | null = null;

        if (viatorData) {
          foundProduct =
            viatorData.pages
              .flatMap((page) => page.data)
              .find((product) => product.product_code === item.id) || null;
        }

        if (!foundProduct) {
          // Fetch from the API if not found in cache
          const response = await api.experiences.get("/viator-products", {
            params: {
              place_id: (item as ItineraryItem).sourceId.viatorProductCode,
              basic: true,
            },
          });
          foundProduct = response.data;
        }

        setSelectedExperienceTabIndex(0);
        setActiveExperiencesTab("activities");
        setViatorProduct(foundProduct);
        setWaytoraExperience(null);

        // Add timeout before opening modal
        setTimeout(() => {
          setIsModalOpen(true);
          setIsFetching(false);
        }, 500);
      } else {
        console.warn(`Item with id ${item.id} not found in any dataset.`);
        return;
      }
    },
    [
      queryClient,
      location.waytora_id,
      location.viator_id,
      setSelectedExperienceId,
      setIsModalOpen,
      setWaytoraExperience,
      setViatorProduct,
      setActiveExperiencesTab,
      setSelectedExperienceTabIndex,
      isModalOpen,
      handleCloseModal,
      setIsFetching,
      setShouldCloseModal,
    ]
  );

  return {
    selectedExperienceId,
    waytoraExperience,
    viatorProduct,
    setWaytoraExperience,
    setViatorProduct,
    isModalOpen,
    setIsModalOpen,
    handleOpenModal,
    handleCloseModal,
    activeExperiencesTab,
    setActiveExperiencesTab,
    selectedExperienceTabIndex,
    setSelectedExperienceTabIndex,
    isFetching,
  };
};
